import { useState } from 'react';
import { Divider, Typography } from '@mui/material';
import styled from 'styled-components';
import { ReactComponent as CloseIcon } from '../../../../assets/close-2.svg';
import { FlexColumn, FlexSpace } from '../../../Flex';
import IconButton from '../../../IconButton';
import useChatRules from '../../hooks/useChatRules';
import RulesSection from './RulesSection';

const CloseIconStyled = styled(CloseIcon)`
  width: 14px;
  height: 14px;
  path {
    fill: ${({ theme }) => theme.palette.common.black};
  }
`;

const RULE_PLACEHOLDER =
  'e.g. Primary keys end with _id; date fields are UTC; never surface PII in examples; open with a TL;DR, then numbered steps.';

const RULE_TOOLTIP =
  'Guide how the agent responds in all chats. Add rules for business logic/context, example usage, PR formatting, when to ask clarifications, and any other global instructions.';

export const PanelHeader = styled(FlexColumn)`
  padding: 24px 24px 0;
  gap: 16px;
  flex-shrink: 0;
`;

export const Sections = styled(FlexColumn)`
  gap: 24px;
  padding: 16px 24px 24px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`;

export const Section = styled(FlexColumn)`
  gap: 16px;
`;

type Props = {
  chatName: string;
  onClose?: () => void;
  hideHeader?: boolean;
};

const ChatSettings = ({ chatName, onClose, hideHeader }: Props) => {
  const [isAddingRule, setIsAddingRule] = useState(false);

  const {
    aiRulesQuery,
    createAiRuleMutation,
    updateAiRuleMutation,
    deleteAiRuleMutation,
  } = useChatRules();

  const rules = aiRulesQuery.data?.rules;

  const handleSave = async (val: string, id?: string) => {
    const content = val.trim();
    const isNewRule = !id;

    if (isNewRule) {
      if (content) {
        await createAiRuleMutation.mutateAsync({ content });
      }
    } else {
      if (content) {
        await updateAiRuleMutation.mutateAsync({
          ruleId: id,
          data: { content },
        });
      } else {
        await deleteAiRuleMutation.mutateAsync(id);
      }
    }

    setIsAddingRule(false);
  };

  const handleDelete = (id: string) => {
    deleteAiRuleMutation.mutateAsync(id);
  };

  return (
    <>
      {!hideHeader && (
        <PanelHeader>
          <FlexSpace>
            <Typography sx={{ fontWeight: 'bold' }}>Chat rules</Typography>
            {onClose && (
              <IconButton onClick={onClose}>
                <CloseIconStyled />
              </IconButton>
            )}
          </FlexSpace>
          <Divider />
        </PanelHeader>
      )}

      <Sections>
        <RulesSection
          title={`${chatName} preferences`}
          tooltip={RULE_TOOLTIP}
          rules={rules}
          isAddingRule={isAddingRule}
          onAddRuleClick={() => setIsAddingRule(true)}
          onSave={handleSave}
          onDelete={handleDelete}
          placeholder={RULE_PLACEHOLDER}
          setIsAddingRule={setIsAddingRule}
        />
      </Sections>
    </>
  );
};

export default ChatSettings;
